import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import { isAuthenticated, signUp } from '../../services/cognitoService';
import './Register.css';

// Age range options (matching mobile)
const AGE_RANGES = [
  { value: '', label: 'Select your age range' },
  { value: '13-17', label: '13-17' },
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45-54', label: '45-54' },
  { value: '55-64', label: '55-64' },
  { value: '65+', label: '65+' },
];

const Register: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const returnTo = (location.state as { from?: string })?.from;

  // Form state (matching mobile structure)
  const [formData, setFormData] = useState({
    email: '',
    ageRange: '',
    password: '',
    confirmPassword: '',
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated()) {
      navigate(returnTo || '/');
    }
  }, [navigate, returnTo]);

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password requirements — each guarded by password.length > 0 to prevent false positives
  const password = formData.password;
  const hasMinLength = password.length > 0 && password.length >= 8;
  const hasUppercase = password.length > 0 && /[A-Z]/.test(password);
  const hasLowercase = password.length > 0 && /[a-z]/.test(password);
  const hasNumber = password.length > 0 && /[0-9]/.test(password);
  const hasSpecialChar = password.length > 0 && /[!@#$%^&*()_+\-=[\]{}|;:',.<>?/~`]/.test(password);

  // Check if passwords match
  const passwordsMatch =
    password.length > 0 &&
    formData.confirmPassword.length > 0 &&
    password === formData.confirmPassword;

  const isPasswordValid =
    hasMinLength && hasUppercase && hasLowercase && hasNumber && hasSpecialChar;

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = t('validation.emailRequired');
    } else if (!isValidEmail(formData.email)) {
      errors.email = t('validation.emailInvalid');
    }

    if (!formData.ageRange) {
      errors.ageRange = t('validation.ageRangeRequired', 'Please select your age range');
    }

    if (!formData.password) {
      errors.password = t('validation.passwordRequired');
    } else if (!isPasswordValid) {
      errors.password = t('validation.passwordRequirements', 'Password does not meet requirements');
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = t(
        'validation.confirmPasswordRequired',
        'Please confirm your password',
      );
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('validation.passwordMismatch', 'Passwords do not match');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Parse and format error messages for better UX
  const formatErrorMessage = (message: string): { title: string; description: string } => {
    // Handle common error patterns
    if (message.includes('already exists') || message.includes('Account already exists')) {
      return {
        title: t('auth.register.errors.accountExists', 'Account Already Exists'),
        description: t(
          'auth.register.errors.accountExistsDesc',
          'An account with this email already exists. Please sign in or use a different email.',
        ),
      };
    }
    if (message.includes('Invalid email') || message.includes('invalid email')) {
      return {
        title: t('auth.register.errors.invalidEmail', 'Invalid Email'),
        description: t(
          'auth.register.errors.invalidEmailDesc',
          'Please enter a valid email address.',
        ),
      };
    }
    if (message.includes('password') && message.includes('policy')) {
      return {
        title: t('auth.register.errors.weakPassword', 'Password Too Weak'),
        description: t(
          'auth.register.errors.weakPasswordDesc',
          'Your password does not meet the security requirements.',
        ),
      };
    }
    if (message.includes('fake email') || message.includes('real email')) {
      return {
        title: t('auth.register.errors.fakeEmail', 'Invalid Email'),
        description: t(
          'auth.register.errors.fakeEmailDesc',
          'Please use a valid, real email address.',
        ),
      };
    }
    if (message.includes('network') || message.includes('Network')) {
      return {
        title: t('auth.register.errors.network', 'Connection Error'),
        description: t(
          'auth.register.errors.networkDesc',
          'Please check your internet connection and try again.',
        ),
      };
    }
    // Default: use the message as-is
    return {
      title: t('auth.register.errors.signupFailed', 'Registration Failed'),
      description: message,
    };
  };

  // Handle signup
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const result = await signUp({
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        givenName: '',
        familyName: '',
        ageRange: formData.ageRange,
      });

      if (result.success) {
        setSuccess(result.message);
        // Navigate to login after successful signup, forwarding return destination
        setTimeout(() => {
          navigate('/login', { state: returnTo ? { from: returnTo } : undefined });
        }, 2000);
      } else {
        setError(result.message);
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : t('auth.register.errors.generic', 'Signup failed. Please try again.'),
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Sign In
  const handleGoogleSignIn = () => {
    // Redirect to Cognito Hosted UI for Google OAuth
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);

    if (cognitoDomain && clientId) {
      if (returnTo) {
        sessionStorage.setItem('theychat-auth-return-url', returnTo);
      }
      window.location.href = `${cognitoDomain}/oauth2/authorize?identity_provider=Google&redirect_uri=${redirectUri}&response_type=code&client_id=${clientId}&scope=openid+email+profile&prompt=login`;
    } else {
      setError('Google sign-in is not configured');
    }
  };

  // Handle Apple Sign In
  const handleAppleSignIn = () => {
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);

    if (cognitoDomain && clientId) {
      if (returnTo) {
        sessionStorage.setItem('theychat-auth-return-url', returnTo);
      }
      window.location.href = `${cognitoDomain}/oauth2/authorize?identity_provider=SignInWithApple&redirect_uri=${redirectUri}&response_type=code&client_id=${clientId}&scope=openid+email+profile`;
    } else {
      setError('Apple sign-in is not configured');
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="register-page">
      {/* Background - same as Landing Page */}
      <div className="bg-accent-shape" />

      <Header />

      <main className="register-main">
        {/* Hero Section */}
        <section className="register-hero">
          <div className="register-hero-content">
            <div className="hero-line"></div>
            <h2 className="hero-label">{t('auth.register.title', 'Registration')}</h2>
            <div className="hero-line"></div>
          </div>
          <h1 className="hero-title">
            {t('auth.register.heroTitle', 'Get started with TheyChat')}
          </h1>
        </section>

        {/* Registration Form Card */}
        <section className="register-card">
          {/* Icon Badge */}
          <div className="register-icon-badge">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <title>User Icon</title>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>

          {/* Already have account link */}
          <div className="register-signin-link">
            <span>{t('auth.register.hasAccount', 'Already have an account?')}</span>
            <Link to="/login" state={returnTo ? { from: returnTo } : undefined}>
              {t('auth.register.signIn', 'Sign in')}
            </Link>
          </div>

          {/* Form Content */}
          <div className="register-form-content">
            <div className="form-header">
              <h3>{t('auth.register.createAccount', 'Create your account')}</h3>
            </div>

            {error &&
              (() => {
                const { title, description } = formatErrorMessage(error);
                return (
                  <div className="alert alert-error">
                    <span className="alert-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Error Icon</title>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    </span>
                    <div className="alert-content">
                      <p className="alert-title">{title}</p>
                      <p className="alert-message">{description}</p>
                    </div>
                  </div>
                );
              })()}

            {success && (
              <div className="alert alert-success">
                <span className="alert-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <title>Success Icon</title>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </span>
                <span>{success}</span>
              </div>
            )}

            <form onSubmit={handleSignUp} className="register-form">
              {/* Email Input */}
              <div className="form-group">
                <label htmlFor="email">{t('auth.register.emailLabel', 'Email')} *</label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={validationErrors.email ? 'error' : ''}
                    placeholder={t('auth.register.emailPlaceholder', 'Enter your email')}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
                {validationErrors.email && (
                  <span className="error-message">{validationErrors.email}</span>
                )}
              </div>

              {/* Age Range Dropdown */}
              <div className="form-group">
                <label htmlFor="ageRange">{t('auth.register.ageRangeLabel', 'Age Range')} *</label>
                <div className="input-wrapper select-wrapper">
                  <select
                    id="ageRange"
                    name="ageRange"
                    value={formData.ageRange}
                    onChange={handleInputChange}
                    className={validationErrors.ageRange ? 'error' : ''}
                    disabled={loading}
                  >
                    {AGE_RANGES.map((range) => (
                      <option key={range.value || 'default'} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                  <svg
                    className="select-arrow"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <title>Select Arrow</title>
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>
                {validationErrors.ageRange && (
                  <span className="error-message">{validationErrors.ageRange}</span>
                )}
              </div>

              {/* Password Input */}
              <div className="form-group">
                <label htmlFor="password">{t('auth.register.passwordLabel', 'Password')} *</label>
                <div className="input-wrapper password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={validationErrors.password ? 'error' : ''}
                    placeholder={t('auth.register.passwordPlaceholder', 'Enter your password')}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        aria-hidden="true"
                      >
                        <title>Show Password</title>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        aria-hidden="true"
                      >
                        <title>Hide Password</title>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>
                {validationErrors.password && (
                  <span className="error-message">{validationErrors.password}</span>
                )}
              </div>

              {/* Confirm Password Input */}
              <div className="form-group">
                <label htmlFor="confirmPassword">
                  {t('auth.register.confirmPasswordLabel', 'Confirm Password')} *
                </label>
                <div className="input-wrapper password-input">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={validationErrors.confirmPassword ? 'error' : ''}
                    placeholder={t(
                      'auth.register.confirmPasswordPlaceholder',
                      'Re-enter your password',
                    )}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        aria-hidden="true"
                      >
                        <title>Show Password</title>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        aria-hidden="true"
                      >
                        <title>Hide Password</title>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>
                {validationErrors.confirmPassword && (
                  <span className="error-message">{validationErrors.confirmPassword}</span>
                )}
              </div>

              {/* Password Requirements */}
              <div className="password-requirements">
                <span className="req-title">
                  {t('auth.register.passwordRequirementsTitle', 'Password Requirements')}
                </span>
                <div className="req-list">
                  <span className={`req ${hasMinLength ? 'met' : ''}`}>
                    {hasMinLength ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <title>Requirement Met</title>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Requirement Not Met</title>
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    {t('validation.password.minLength', 'At least 8 characters')}
                  </span>
                  <span className={`req ${hasUppercase ? 'met' : ''}`}>
                    {hasUppercase ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <title>Requirement Met</title>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Requirement Not Met</title>
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    {t('validation.password.uppercase', 'One uppercase letter')}
                  </span>
                  <span className={`req ${hasLowercase ? 'met' : ''}`}>
                    {hasLowercase ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <title>Requirement Met</title>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Requirement Not Met</title>
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    {t('validation.password.lowercase', 'One lowercase letter')}
                  </span>
                  <span className={`req ${hasNumber ? 'met' : ''}`}>
                    {hasNumber ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <title>Requirement Met</title>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Requirement Not Met</title>
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    {t('validation.password.number', 'One number')}
                  </span>
                  <span className={`req ${hasSpecialChar ? 'met' : ''}`}>
                    {hasSpecialChar ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <title>Requirement Met</title>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Requirement Not Met</title>
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    {t('validation.password.specialChar', 'One special character (!@#$%...)')}
                  </span>
                  <span className={`req ${passwordsMatch ? 'met' : ''}`}>
                    {passwordsMatch ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <title>Requirement Met</title>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Requirement Not Met</title>
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    {t('validation.password.match', 'Passwords match')}
                  </span>
                </div>
              </div>

              {/* Terms Text (not checkbox - matching mobile) */}
              <div className="terms-text">
                <span>
                  {t('auth.register.termsPrefix', 'By signing up I agree to the')}{' '}
                  <Link to="/terms">{t('auth.register.termsOfUse', 'terms of use')}</Link>
                  {t('auth.register.and', ' and ')}{' '}
                  <Link to="/privacy">{t('auth.register.privacyPolicy', 'Privacy Policy')}</Link>
                </span>
              </div>

              {/* Submit Button */}
              <div className="form-actions">
                <button type="submit" className="btn-register" disabled={loading}>
                  {loading
                    ? t('auth.register.signingUp', 'Signing up...')
                    : t('auth.register.signUpButton', 'Sign Up')}
                </button>
              </div>
            </form>

            {/* Divider */}
            <div className="auth-divider">
              <span>{t('auth.register.orContinueWith', 'or continue with')}</span>
            </div>

            {/* Google Sign In */}
            <button
              type="button"
              className="btn-google"
              onClick={handleGoogleSignIn}
              disabled={loading}
              id="register-google-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                <title>Google Icon</title>
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>{t('auth.register.continueWithGoogle', 'Continue with Google')}</span>
            </button>

            {/* Apple Sign In */}
            <button
              type="button"
              className="btn-apple"
              onClick={handleAppleSignIn}
              disabled={loading}
              id="register-apple-btn"
            >
              <svg width="18" height="22" viewBox="0 0 814 1000" aria-hidden="true" fill="white">
                <title>Apple Icon</title>
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.3-150.3-107.9C27.8 746.6 0 634.6 0 529.2c0-170.3 111-260.3 220-260.3 56.5 0 103.4 37.3 138.8 37.3 33.7 0 87.3-39.5 151.7-39.5 24.4 0 108.2 2.6 168 75.5zm-126.5-185c-7.1 32.8-21.4 65.7-43.6 92.5-28.8 35.2-69.3 62.8-108.2 57.5 0-2.6-.6-5.2-.6-7.8 0-34.5 14.3-70.3 36.5-95.2 21.4-24.8 61.6-43.9 95.4-52.8 1.3.1 2.6.1 3.9.1.1 2.5.8 5.1.6 5.7z" />
              </svg>
              <span>{t('auth.register.continueWithApple', 'Continue with Apple')}</span>
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Register;
